import React, { useState, useRef } from "react";
import { Col, Row, Alert } from "reactstrap";
import _ from "lodash";
import * as SchemaLib from "@repoflow/schema";
import {
  Content as ContentAvailable,
} from "@repoflow/components/content-available";

import * as Loading from "@repoflow/components/loading";
import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { useVisibility } from "@repoflow/components/visibility";

import { ObjectComponent, getField } from "@repoflow/form-schema/widgets/object";

import {
  Provider as SchemaProvider,
  useSchemaContext,
} from "@repoflow/form-schema/schema-config";
import "./style.css";

export const getListOffset = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id");
  const updatedAt = queryParams.get("updatedAt");

  return id && updatedAt ? { id, updatedAt } : null;
};

export const List = (props) => {
  const { schema, config, widgets: outterWidgets } = props;

  return (
    <SchemaProvider schema={schema} widgets={outterWidgets} config={config}>
      <ListHandler {...props} />
    </SchemaProvider>
  );
};

const ListHandler = (props) => {
  const {
    mode = "table",
    query,
    variables,
    target,
    attributes,
    component: Component,
    content: Content,
    path,
    onData,
    orderBy: initialOrderBy = "updatedAt",
    orderMode: initialOrderMode = "desc",
    height,
    wrapper: Wrapper = React.Fragment,
    listTag = "list",
    fetchPolicy,
    mapItem,
    orderFields = [],
  } = props;

  const [state, setState] = useState({
    locked: true,
    current: null,
    result: null,
    orderBy: initialOrderBy,
    orderMode: initialOrderMode,
  });

  const { orderBy, orderMode, locked, current } = state;
  const toggleOrderMode = () =>
    setState((state) => ({
      ...state,
      orderMode: orderMode === "asc" ? "desc" : "asc",
      result: null,
      current: null,
    }));

  const setOrderBy = (orderBy) =>
    setState((state) => ({ ...state, orderBy, result: null, current: null }));

  const { loading, error, data, refetch } = useQuery(query, {
    fetchPolicy,
    variables: {
      ...variables,
      input: {
        last: state.current,
        orderBy,
        orderMode,
      },
    },
  });

  const getResults = ({ result }) =>
    _.map(
      _.keys(result),
      /*orderMode === "asc"
        ? _.keys(result).sort()
        : _.keys(result).sort().reverse()*/ (k) => result[k]
    ).flat();

  const list = data ? _.get(data, path + "." + listTag) : null;

  const scrollHandler = (e) => {
    if (data && list.last) {
      const tgt = e.target;

      var scrollTop = tgt.scrollTop;
      var scrollHeight = tgt.scrollHeight; // added
      var offsetHeight = tgt.offsetHeight;

      var contentHeight = scrollHeight - offsetHeight; // added

      if (contentHeight <= scrollTop + 1) {
        //setState((state) => ({ ...state, current: list.last }));
      }
    }
  };

  const {
    isVisible,
    marker,
    update: updateVisibility,
    reset: resetVisibility,
  } = useVisibility();

  useEffect(() => {
    if (isVisible && !loading && locked === false && list.last) {
      setState((state) => ({ ...state, current: list.last, locked: true }));
    }
  }, [isVisible, loading, locked]);

  useEffect(() => {
    updateVisibility();
  }, [loading, locked]);

  useEffect(() => {
    //console.log(data);
    if (data) {
      const list = _.get(data, path + "." + listTag);
      //console.log(list.id);
      resetVisibility();
      setState((state) => ({
        ...state,
        result: {
          ...state.result,
          [list.id]: mapItem ? list.items.map(mapItem) : list.items,
        },
        data,
      }));
    }
  }, [data]);

  useEffect(() => {
    if (state.result && data) {
      const results = getResults(state);
      onData && onData(results, data);

      setState((state) => ({
        ...state,
        locked: false,
      }));
    }
  }, [state.result]);

  // The raw GraphQL message used to be returned as a bare string in place of
  // the whole list, which reads as a stray line of text on the page and offers
  // no way to retry. The detail goes to the console instead.
  if (error) {
    console.error("list:", error);
    return (
      <Alert color="danger">
        This list could not be loaded.{" "}
        <button
          type="button"
          className="btn btn-link p-0 align-baseline"
          onClick={() => refetch()}
        >
          Try again
        </button>
      </Alert>
    );
  }

  return (
    <>
      <ContentAvailable
        className={"container"}
        height={height}
        onScroll={scrollHandler}
      >
        <HandleResults
          state={state}
          toggleOrderMode={toggleOrderMode}
          setOrderBy={setOrderBy}
          getResults={getResults}
          loading={loading}
          data={data}
          list={list}
          {...props}
        />
        {/* The next page loads when this marker scrolls into view. It is found
            by its position alone, so it holds no content; a stray "." here was
            rendered under every list. */}
        <div ref={marker}></div>
      </ContentAvailable>
    </>
  );
};

const HandleResults = (props) => {
  const {
    state,
    toggleOrderMode,
    setOrderBy,

    getResults,
    mode = "table",
    query,
    variables,
    target,
    attributes,
    component: Component,
    content: Content,
    path,
    onData,
    orderBy: initialOrderBy = "updatedAt",
    orderMode: initialOrderMode = "desc",
    height,
    wrapper: Wrapper = React.Fragment,
    listTag = "list",
    fetchPolicy,
    mapItem,
    orderFields = [],
    loading,
    data,
    list,
    empty = "Nothing here yet.",
  } = props;

  const context = useSchemaContext();
  const { config, schema } = context;

  const { orderBy, orderMode } = state;

  if (!state.result) {
    return (
      <div className="mt-4">
        <Loading.Content />
      </div>
    );
  }

  const results = getResults(state);

  const resolvedType =
    target && schema ? SchemaLib.resolve(schema, target) : null;
  const itemsField = resolvedType
    ? getField("listing", resolvedType, { parent: null, values: null }, context)
    : [];

  const targetConfig = resolvedType
    ? config
      ? config[resolvedType.id]
      : null
    : null;
  const targetFields =
    targetConfig && targetConfig.fields
      ? targetConfig.fields
      : resolvedType && itemsField
      ? itemsField.fields.map(({ name }) => name)
      : null;
  const fieldColumns =
    resolvedType && itemsField && targetFields
      ? _.reduce(
          targetFields,
          (res, fieldConfig) => {
            const fieldProp = _.find(itemsField.fields, (field) => {
              return (
                fieldConfig === field.name ||
                fieldConfig.property === field.name
              );
            });

            if (!fieldProp) {
              throw new Error(
                "invalid.config:" +
                  (fieldConfig ? fieldConfig.property : fieldConfig)
              );
            }

            res.push(fieldProp);
            return res;
          },
          []
        )
      : null;

  const contentProps = {
    attributes,
    variables,
    data: state.data,
  };

  return (
    <>
      {mode === "table" ? (
        <div className="table-responsive-md header-fixed">
          <table className="table table-striped table-sm">
            <thead className="thead-dark">
              <tr>
                {Content && <th></th>}
                {itemsField ? (
                  _.map(fieldColumns, (field, idx) => {
                    const { name, property, config } = field;

                    return (
                      <th key={idx}>
                        {name === orderBy && (
                          <button
                            onClick={() => toggleOrderMode()}
                            className="btn btn-link"
                          >
                            {orderMode === "asc" ? (
                              <i className="fa fa-sort-asc"></i>
                            ) : (
                              <i className="fa fa-sort-desc"></i>
                            )}
                          </button>
                        )}
                        {name !== orderBy && orderFields.includes(name) && (
                          <button
                            onClick={() => setOrderBy(name)}
                            className="btn btn-link"
                          >
                            <i className="fa fa-sort"></i>
                          </button>
                        )}{" "}
                        {(config && config.label) || property.label || name}
                      </th>
                    );
                  })
                ) : (
                  <th></th>
                )}
              </tr>
            </thead>

            <tbody>
              {results.length === 0 && !loading ? (
                <tr>
                  <td
                    className="text-muted text-center"
                    colSpan={
                      (fieldColumns ? fieldColumns.length : 1) +
                      (Content ? 1 : 0)
                    }
                  >
                    {empty}
                  </td>
                </tr>
              ) : null}
              {_.map(results, (item) => {
                const { id } = item;
                return (
                  <tr key={id}>
                    {Content ? (
                      <td>
                        <Content {...contentProps} item={item} />
                      </td>
                    ) : null}

                    <ObjectComponent
                      mode={mode}
                      target={target}
                      parent={null}
                      value={item}
                      readOnly={true}
                    />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Wrapper>
          {results.length === 0 && !loading ? (
            <div className="text-muted">{empty}</div>
          ) : null}
          {_.map(results, (item) => {
            const { id } = item;
            return Component ? (
              <Component
                {...contentProps}
                key={id}
                item={item}
                content={Content}
              />
            ) : (
              <React.Fragment key={id}>
                <Row>
                  <Col md={Content ? 8 : 12}>
                    <ObjectComponent
                      target={target}
                      parent={null}
                      value={item}
                      readOnly={true}
                    />
                  </Col>
                  {Content ? (
                    <Col>
                      <Content {...contentProps} item={item} />
                    </Col>
                  ) : null}
                </Row>
                <hr />
              </React.Fragment>
            );
          })}
        </Wrapper>
      )}

      {loading && (
        <div className="mt-4">
          <Loading.Content />
        </div>
      )}

      {list && !list.last && (
        <Row>
          <Col>
            <div className="text-center">End of results</div>
          </Col>
        </Row>
      )}
    </>
  );
};

/*
<Button
                    onClick={() => {
                      //control.setCurrent(list.last);

                      //getListOffset
                      history.push(``)

                      history.push({
                        pathname: '/secondpage',
                        search: '?query=abc',
                        state: { detail: 'some_value' }
                    });
                    }}
                  >
                    {">>"}
                  </Button>

                  <Container className="mb-2">
        <Row>
          <Col>
            <div className="float-right">
              {list.last && (
                <Link
                  to={
                    location.pathname +
                    `?id=${list.last.id}&updatedAt=${list.last.updatedAt}`
                  }
                >
                  {">>"}
                </Link>
              )}
            </div>
          </Col>
        </Row>
      </Container>


       {false && list.last && (
          <Row>
            <Col>
              <div className="text-center">
                <Button
                  onClick={() => {
                    setState((state) => ({ ...state, current: list.last }));
                  }}
                >
                  {"Load more..."}
                </Button>
              </div>
            </Col>
          </Row>
        )}
*/
